import { autorun, makeAutoObservable } from 'mobx';
import { inject, injectable } from 'inversify';
import { ProfileModel } from '@mobx/services/profile/model';
import { AppartmentParsedData, User } from '@mobx/interfaces';
import { clearPersistedStore, makePersistable } from 'mobx-persist-store';
import { omit } from 'lodash';
import logger from 'js-logger';
import { type AuthService, AuthServiceIdentifier } from '@shared/types/mobx/services/auth';
import { type RolesService, RolesServiceIdentifier } from '@shared/types/mobx/services/roles';
import { initialUser } from '../../initial';

@injectable()
export class ProfileModule {
  userData: User = initialUser;

  // eslint-disable-next-line no-unused-vars
  constructor(
    private model: ProfileModel,
    @inject(AuthServiceIdentifier) private authModule: AuthService,
    @inject(RolesServiceIdentifier) private rolesModule: RolesService
  ) {
    makeAutoObservable(this);
    makePersistable(this, { name: 'ProfileModule', properties: ['userData'] });

    autorun(() => {
      if (!this.authModule.userData?.id) {
        return;
      }
      this.userData.id = this.authModule.userData?.id;
    });

    autorun(() => {
      this.userData.info = this.authModule.userData;
    });

    autorun(() => {
      if (!this.rolesModule.currentRole) return;

      this.userData.currentRole = this.rolesModule.currentRole;

      switch (this.userData.currentRole.role) {
        case 'CHAIRMAN':
        case 'CHAIRMAN_TRUSTED':
        case 'CHAIRMAN_TRUSTED_READONLY': {
          this.loadChairmanData();
          break;
        }
        case 'ABONENT': {
          this.loadAbonentData();
          break;
        }
        case 'OPERATOR': {
          this.loadChairmanData();
          this.loadAbonentData();
          break;
        }
        default: {
          break;
        }
      }
    });
  }

  refreshUserInfo = async () => {
    try {
      await this.authModule.refreshUserInfo();
    } catch (e: any) {
      logger.error(e);
    }
  };

  // Чистим persist сессии: сбрасываем userData к initialUser (в т.ч. currentRole → ABONENT-объект),
  // затем удаляем localStorage-ключ. Сброс идёт через присвоение всего объекта, т.к. autorun
  // при currentRole=null делает ранний return и не обнулил бы поле. Иначе данные профиля/роль
  // протекают между входами.
  clearSession = async () => {
    this.userData = initialUser;
    await clearPersistedStore(this);
  };

  private async loadChairmanData() {
    if (!this.authModule.id) return;

    this.userData.osis = (await this.model.getUserOsi(this.authModule.id))
      .filter((osi) => osi != null)
      .sort((a, b) => a.id - b.id);
  }

  private async loadAbonentData() {
    if (!this.authModule.id) return;

    const appartments = (await this.model.getUserApartment(this.userData.id)).sort((a, b) => a.osiId - b.osiId);

    if (Array.isArray(appartments)) {
      const list: AppartmentParsedData[] = [];
      appartments.forEach((a) => {
        const { abonents = [] } = a;
        abonents.forEach((b) => {
          list.push(omit({ ...b, ...a }, ['abonents']));
        });
      });
      this.userData.appartments = list;
    } else this.userData.appartments = [];
  }
}
